import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { useLocalizedStrings } from "../LocalizationProvider";
import { cn } from "../utils/tailwind";
import { FilterPanel } from "./FilterPanel";
import { FilterBadges } from "./FilterBadges";
function clauseAtPath(group, path) {
    const [head, ...rest] = path;
    if (head == null)
        return undefined;
    const clause = group.conditions[head];
    if (clause == null)
        return undefined;
    if (rest.length === 0)
        return clause;
    if (!('conditions' in clause))
        return undefined;
    return clauseAtPath(clause, rest);
}
function removeClause(group, target) {
    const conditions = group.conditions.flatMap((clause) => {
        if (clause === target)
            return [];
        if ('conditions' in clause) {
            const updated = removeClause(clause, target);
            return updated.conditions.length > 0 ? [updated] : [];
        }
        return [clause];
    });
    return { ...group, conditions };
}
export function useFilterPanel(props, externalRef) {
    const L = useLocalizedStrings();
    const internalRef = useRef(null);
    const filterRef = externalRef || internalRef;
    const [isOpen, setIsOpen] = useState(false);
    const [liveConfig, setLiveConfig] = useState(props.value);
    const [appliedConfig, setAppliedConfig] = useState(props.value);
    const appliedConfigRef = useRef(props.value);
    useEffect(() => {
        appliedConfigRef.current = appliedConfig;
    }, [appliedConfig]);
    useEffect(() => {
        setAppliedConfig(props.value);
        setLiveConfig(props.value);
        appliedConfigRef.current = props.value;
    }, [props.value]);
    const deferredMode = !!props.onConfirm;
    const countSource = deferredMode ? appliedConfig : liveConfig;
    const activeFilterCount = useMemo(() => {
        const countConditions = (conditions) => {
            if (!conditions)
                return 0;
            return conditions.reduce((count, c) => 'field' in c ? count + 1 : count + countConditions(c.conditions), 0);
        };
        return countConditions(countSource?.config?.conditions);
    }, [countSource]);
    const hasActiveFilters = activeFilterCount > 0;
    const handleOpenChange = useCallback((open) => {
        setIsOpen(open);
        props.onOpenChange?.(open);
    }, [props.onOpenChange]);
    const handleChange = useCallback((config) => {
        setLiveConfig(config);
        if (!deferredMode) {
            props.onChange?.(config);
        }
    }, [props.onChange, deferredMode]);
    const handleConfirm = useCallback((config) => {
        appliedConfigRef.current = config;
        setAppliedConfig(config);
        setLiveConfig(config);
        props.onChange?.(config);
        props.onConfirm?.(config);
    }, [props.onChange, props.onConfirm]);
    const handleRemoveCondition = useCallback((path) => {
        const renderedRoot = appliedConfig?.config;
        if (!renderedRoot)
            return;
        const target = clauseAtPath(renderedRoot, path);
        if (!target)
            return;
        const latestRoot = appliedConfigRef.current?.config ?? renderedRoot;
        const newRoot = removeClause(latestRoot, target);
        const newConfig = newRoot.conditions.length > 0
            ? { enabled: true, config: newRoot }
            : { enabled: false };
        appliedConfigRef.current = newConfig;
        filterRef.current?.applyConfig(newConfig);
    }, [appliedConfig, filterRef]);
    const triggerButton = (_jsx(Button, { className: cn("transition-colors duration-300 text-left flex-grow rounded-md ring-[1px]", hasActiveFilters
            ? "ring-[2px] ring-[var(--ak-identity-color-blue-2)] bg-[var(--ak-identity-color-blue-50)] text-[var(--ak-identity-color-blue-4)] hover:bg-[var(--ak-identity-color-blue-100,theme(colors.blue.100))] focus-visible:bg-[var(--ak-identity-color-blue-100,theme(colors.blue.100))]"
            : "ring-gray-300 bg-[#FFFFFF] hover:bg-[var(--ak-color-button-secondary-background-hover)] focus-visible:bg-[var(--ak-color-button-secondary-background-hover)]", isOpen && "ring-2 ring-[var(--ak-identity-color-blue-2)]"), variant: "unstyled", icon: _jsx(Icon, { name: isOpen ? "ChevronUpIcon" : "ChevronDownIcon", type: "outline", className: "flex-shrink-0" }), size: "md", onClick: () => filterRef.current?.toggleOpen(), children: _jsxs("div", { className: "inline-flex gap-x-2", children: [_jsx("span", { children: L.component_library.filter_panel.filters }), hasActiveFilters && (_jsx("span", { className: "border-l-[1px] border-[var(--ak-identity-color-blue-200)] pl-2", children: activeFilterCount }))] }) }));
    const filterContent = (_jsxs(_Fragment, { children: [_jsx(FilterPanel, { ...props, ref: filterRef, onOpenChange: handleOpenChange, onChange: handleChange, onConfirm: deferredMode ? handleConfirm : undefined }), hasActiveFilters && (_jsx("div", { className: "p-2", children: _jsx(FilterBadges, { config: appliedConfig, availableFields: props.availableFields, showApplicantGroupField: props.showApplicantGroupField, onRemoveCondition: handleRemoveCondition, formatFilterValue: props.formatFilterValue }) })), isOpen && _jsx("div", { className: "py-2" })] }));
    return {
        triggerButton,
        filterContent,
        isOpen,
        hasActiveFilters,
        activeFilterCount,
        filterRef,
    };
}
